import { createContext, use, useEffect, useId } from "react"

/*
 * Whether anything on the page holds work someone has written and not yet saved: a description or
 * a comment being edited, a new comment, a spec being reshaped. Each editor says so for itself
 * with `useUnsavedWork`, for as long as it is mounted, and whatever is about to throw the page's
 * work away (closing the Ticket peek, leaving the page) asks `hasUnsavedWork()` first.
 *
 * It is one registry for the page rather than a context, so an editor reports wherever it is
 * rendered and a reader asks wherever it is, outside React too. What someone wrote is never kept
 * here, only that it exists; keeping it across a reload is a different job.
 *
 * An editor inside `UnsavedWorkApart` records nothing: the Design system page's specimens, which
 * show a SaveBar or an editor for as long as the page is open, hold no work of anyone's.
 */

/** The editors holding unsaved work now, each by its own id. */
const holding = new Set<string>()

/**
 * Records whether `editor` holds unsaved work, and gives back what takes the record away again,
 * as an effect's cleanup does when the editor changes its answer or goes.
 */
export function markUnsavedWork(editor: string, unsaved: boolean) {
  if (!unsaved) return () => undefined
  holding.add(editor)
  return () => void holding.delete(editor)
}

/** Whether the editors inside hold no work of the page's, as a specimen doesn't. */
export const UnsavedWorkApart = createContext(false)

/** Says whether this editor holds unsaved work, until it says otherwise or goes. */
export function useUnsavedWork(unsaved: boolean) {
  const editor = useId()
  const apart = use(UnsavedWorkApart)
  useEffect(() => markUnsavedWork(editor, unsaved && !apart), [editor, unsaved, apart])
}

/** Whether any editor on the page holds unsaved work now. */
export function hasUnsavedWork() {
  return holding.size > 0
}

/** Where the browser is, or is going, as a guard over unsaved work reads it. */
export type At = { pathname: string; search: string }

/**
 * Whether going from one address to the next leaves the page, taking its editors with it: another
 * path does; the same path with other parameters, or another hash, keeps the page.
 */
export const leavesPage = (from: At, to: At) => from.pathname !== to.pathname
